import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { CreditCard, Settings } from 'lucide-react';
import { StripeConnectButton } from './StripeConnectButton';
import { CommissionRatesTable } from './CommissionRatesTable';

interface PaymentManagementDialogProps {
  entityType: 'organization' | 'club';
  entityId: string;
  entityName: string;
  children?: React.ReactNode;
}

export const PaymentManagementDialog = ({ 
  entityType, 
  entityId, 
  entityName,
  children 
}: PaymentManagementDialogProps) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <Button variant="outline" className="w-full">
            <CreditCard className="w-4 h-4 mr-2" />
            Manage Payments
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Management - {entityName}
          </DialogTitle>
          <DialogDescription>
            Configure payment processing and commission rates for this {entityType}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="setup" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="setup" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payment Setup
            </TabsTrigger>
            <TabsTrigger value="rates" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Commission Rates
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="setup" className="space-y-4 mt-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Stripe Integration</h3>
              <p className="text-sm text-muted-foreground">
                Connect this {entityType} to Stripe for payment processing
              </p>
            </div>
            <StripeConnectButton
              entityType={entityType}
              entityId={entityId}
              entityName={entityName}
              size="lg"
            />
          </TabsContent>
          
          <TabsContent value="rates" className="space-y-4 mt-6 overflow-auto max-h-[500px]">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Commission Configuration</h3>
              <p className="text-sm text-muted-foreground">
                Configure custom commission rates for this {entityType}
              </p>
            </div>
            <CommissionRatesTable
              organizationId={entityType === 'organization' ? entityId : undefined}
              clubId={entityType === 'club' ? entityId : undefined}
              title={`${entityName} Commission Rates`}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};